import { useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import { appAPI } from '../../utils/api';
import { getIntl } from '../../utils/intl';
import urls from '../../utils/urls';
import { getAllProfileInAppNotificationsCountKey } from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';

const intl = getIntl('useProfileUnreadInAppNotificationCount', {
    loadError: 'Error loading notification counts',
});

export type UseProfileUnreadInAppNotificationCountArgs = {
    medicalProfileId: string;
};

export type UseProfileUnreadInAppNotificationCountResponse = {
    unreadInAppNotificationCount: number;
    isLoading: boolean;
};

export const useProfileUnreadInAppNotificationCount = ({
    medicalProfileId,
}: UseProfileUnreadInAppNotificationCountArgs): UseProfileUnreadInAppNotificationCountResponse => {
    const profilesUnreadInAppNotificationCountQuery = useQuery({
        queryKey: getAllProfileInAppNotificationsCountKey(),
        queryFn: async () => {
            const response =
                await appAPI.get<APITypes.medicalProfileService.GetProfilesUnreadInAppNotificationsCountResponse>(
                    urls.api.medicalProfiles.inAppNotifications.allProfileCounts,
                );
            return response;
        },
    });
    useQueryErrorNotify(profilesUnreadInAppNotificationCountQuery, intl('loadError'));

    const count =
        profilesUnreadInAppNotificationCountQuery.data?.profilesInAppNotificationsCount.find(
            (profile) => profile.medicalProfileId === medicalProfileId,
        )?.count || 0;

    return {
        unreadInAppNotificationCount: count,
        isLoading: profilesUnreadInAppNotificationCountQuery.isLoading,
    };
};
